<template>
  <div class="relative inline-block">
    <button
      ref="trigger"
      type="button"
      class="p-0 border-0 bg-transparent rounded-full w-8 h-8 flex items-center justify-center cursor-pointer"
      @click.stop="togglePopover"
      :aria-label="ariaLabelComputed"
      @keydown.enter.stop.prevent="togglePopover"
      @keydown.space.stop.prevent="togglePopover"
    >
      <span
        v-html="moreOptionSvg({})"
        class="text-blueGrey-blueGrey cursor-pointer"
      ></span>
    </button>
    <Popover
      ref="popover"
      :target="popoverTarget"
      :show-close-icon="false"
      class="w-[200px] sm:w-[272px] rounded-t-2 shadow-lg p-0 overflow-hidden !m-0 rounded-t-[8px]"
      @hide="onHide"
      @show="onShow"
      dismissable
      role="menu"
      aria-label="{{ $t('commonLabels.actionMenu') }}"
    >
      <div @keydown="handleMenuKeydown">
        <div
          v-if="header"
          class="max-h-[35px] flex items-center justify-between bg-greyLight text-white px-3 py-4 rounded-t-[8px] rounded-b-[0]"
        >
          <span
            class="font-just font-medium text-[14px] leading-[100%] tracking-[0%]"
            >{{ header }}</span
          >
          <button
            ref="closeButtonRef"
            class="border-none bg-transparent justify-center border-0 text-white text-xl cursor-pointer p-0 flex items-center"
            @click="closePopover"
            @keydown.enter="closePopover"
            @keydown.space.prevent="closePopover"
            aria-label="{{ $t('accessibilityLabels.closeMenu') }}"
            v-html="cancelSvg"
            data-test-id="cancelSvg"
            tabindex="0"
          ></button>
        </div>
        <slot :close="closePopover">
          <template v-if="items && items.length">
            <ul class="list-none m-0 p-0">
              <!-- NOSONAR -->
              <li
                v-for="(item, idx) in items"
                :key="idx"
                :class="[
                  ' flex items-center gap-3 px-3 py-4 cursor-pointer border-t-0 border-x-0 border-[#D9D9D9] text-base bg-white transition',
                  confirmingIdx !== null && confirmingIdx !== idx
                    ? 'text-gray-300 !cursor-not-allowed opacity-30'
                    : '',
                  item.danger ? 'text-[#a94442]' : '',
                  !item.disabled && confirmingIdx === null
                    ? 'hover:bg-gray-100'
                    : '',
                  idx === items.length - 1 ? '' : 'border-b border-solid',
                  item.class || '',
                  confirmingIdx === idx ? 'h-[71px]' : 'h-[62px]',
                ]"
                @click="
                  confirmingIdx === null || confirmingIdx === idx
                    ? handleItemClick(item, idx, closePopover)
                    : null
                "
                @keydown.enter="
                  confirmingIdx === null || confirmingIdx === idx
                    ? handleItemClick(item, idx, closePopover)
                    : null
                "
                :disabled="
                  item.disabled ||
                  (confirmingIdx !== null && confirmingIdx !== idx)
                "
                tabindex="0"
                ref="menuItemsRef"
              >
                <template v-if="confirmingIdx === idx">
                  <div class="flex flex-col w-full items-center cursor-default">
                    <span
                      class="font-just font-500 text-[16px] leading-[100%] tracking-[0%] text-alert mb-2"
                      >{{ $t('commonLabels.areYouSure') }}</span
                    >
                    <div class="flex gap-2 w-full">
                      <button
                        ref="cancelConfirmButton"
                        style="background: rgba(138, 151, 152, 0.1)"
                        class="flex-1 cursor-pointer h-[26px] py-1 px-2 font-just font-semibold text-[14px] rounded border-none leading-[100%] tracking-[0%] text-softGray"
                        @click.stop="handleCancelConfirm(closePopover)"
                        @keydown.enter.stop="handleCancelConfirm(closePopover)"
                        @keydown.space.prevent.stop="handleCancelConfirm(closePopover)"
                        tabindex="0"
                        aria-label="{{ $t('commonLabels.noCancel') }}"
                      >
                        {{ $t('commonLabels.noCancel') }}
                      </button>
                      <button
                        ref="confirmButton"
                        style="background: rgba(134, 51, 41, 0.1)"
                        class="flex-1 cursor-pointer h-[26px] py-1 px-2 font-just font-semibold text-[14px] rounded border-none leading-[100%] tracking-[0%] text-alert"
                        @click.stop="handleConfirm(idx, closePopover, item)"
                        @keydown.enter.stop="handleConfirm(idx, closePopover, item)"
                        @keydown.space.prevent.stop="handleConfirm(idx, closePopover, item)"
                        tabindex="0"
                        aria-label="{{ $t('commonLabels.yesDelete') }}"
                      >
                        {{ $t('commonLabels.yesDelete') }}
                      </button>
                    </div>
                  </div>
                </template>
                <template v-else>
                  <span
                    v-if="item.icon"
                    class="w-5 h-5"
                    v-html="item.icon"
                    role="img"
                  ></span>
                  <span
                    :class="[
                      item?.class,
                      'flex-1 text-btnActive font-just font-medium text-[16px] leading-[100%] tracking-[0%]',
                      ,
                    ]"
                    >{{ item.label }}</span
                  >
                </template>
              </li>
            </ul>
          </template>
        </slot>
      </div>
    </Popover>
  </div>
</template>

<script setup>
import { ref, computed, nextTick } from "vue";
import { useI18n } from "vue-i18n";
import Button from "primevue/button";
import Popover from "primevue/popover";
import { closeSvg, moreOptionSvg } from "@/utils/commonSvg";
import { usePopoverConfirmation } from "@/composables/usePopoverConfirmation";

const { t } = useI18n();

const cancelSvg = computed(() =>
  closeSvg({ width: "24px", height: "24px", color: "white" })
);

const emit = defineEmits(["deleted"]);

const props = defineProps({
  items: {
    type: Array,
    default: undefined,
  },
  header: {
    type: String,
    default: "",
  },
  ariaLabel: {
    type: String,
    default: "",
  },
});

const ariaLabelComputed = computed(() => {
  return props.ariaLabel || t('commonLabels.moreOptions');
});

const trigger = ref(null);
const closeButton = ref(null);
const cancelConfirmButton = ref(null);
const confirmButton = ref(null);
const menuItemRefs = ref([]);
const isPopoverVisible = ref(false);
const focusedIndex = ref(0);

const {
  popover,
  popoverTarget,
  confirmingIdx,
  togglePopover,
  closePopover,
  onHide,
  handleItemClick,
  handleConfirm,
  handleCancelConfirm,
} = usePopoverConfirmation(trigger, emit, {
  onConfirm: (item, close) => {
    if (item.onConfirm) {
      item.onConfirm(close);
    } else {
      close();
      emit("deleted", item);
    }
  },
  onCancel: (close) => {
    close();
  }
});

const closeButtonRef = ref(null);
const menuItemsRef = ref([]);

const onShow = async () => {
  await nextTick();
  if (closeButtonRef.value) {
    closeButtonRef.value.focus();
  } else if (menuItemsRef.value && menuItemsRef.value.length > 0) {
    const firstItem = menuItemsRef.value[0];
    if (firstItem) {
      firstItem.focus();
    }
  }
};
</script>

<style>
.p-popover .p-popover-content {
  padding: 0;
}
</style>
